/**
 * ThreePartProgress Component
 *
 * TurboTax-style three-part progress indicator showing:
 * - Data Completeness (fields answered)
 * - Packet Completeness (documents present)
 * - Compliance Readiness (checks passing)
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ClipboardCheck,
  FileStack,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProgressSection {
  percentage: number;
  missingItems?: string[];
  label?: string;
}

interface ThreePartProgressProps {
  data: ProgressSection;
  packet: ProgressSection;
  compliance: ProgressSection;
  className?: string;
  compact?: boolean;
}

function getProgressColor(percentage: number): string {
  if (percentage >= 100) return 'bg-green-500';
  if (percentage >= 75) return 'bg-blue-500';
  if (percentage >= 50) return 'bg-yellow-500';
  if (percentage >= 25) return 'bg-orange-500';
  return 'bg-red-500';
}

function getStatusIcon(percentage: number) {
  if (percentage >= 100) {
    return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  }
  if (percentage >= 75) {
    return <Info className="h-4 w-4 text-blue-500" />;
  }
  return <AlertCircle className="h-4 w-4 text-amber-500" />;
}

function ProgressRow({
  icon: Icon,
  title,
  description,
  percentage,
  missingItems,
  compact,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  percentage: number;
  missingItems?: string[];
  compact?: boolean;
}) {
  const progressColor = getProgressColor(percentage);
  const statusIcon = getStatusIcon(percentage);
  const hasMissing = missingItems && missingItems.length > 0;

  return (
    <div className={cn('space-y-2', compact ? 'py-2' : 'py-3')}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={cn('text-muted-foreground', compact ? 'h-4 w-4' : 'h-5 w-5')} />
          <div>
            <div className={cn('font-medium', compact ? 'text-sm' : 'text-base')}>
              {title}
            </div>
            {!compact && (
              <div className="text-xs text-muted-foreground">{description}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {statusIcon}
          <Badge variant={percentage >= 100 ? 'default' : 'secondary'} className="font-mono">
            {percentage}%
          </Badge>
        </div>
      </div>

      <div className="relative">
        <Progress
          value={percentage}
          className={cn('h-2', compact ? 'h-1.5' : 'h-2')}
        />
        <div
          className={cn(
            'absolute top-0 left-0 h-full rounded-full transition-all',
            progressColor
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {hasMissing && !compact && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-xs text-muted-foreground flex items-center gap-1 cursor-help">
                <AlertCircle className="h-3 w-3" />
                <span>
                  Missing: {missingItems.slice(0, 2).join(', ')}
                  {missingItems.length > 2 && ` +${missingItems.length - 2} more`}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="font-medium mb-1">Missing items:</p>
              <ul className="text-xs space-y-0.5">
                {missingItems.map((item, i) => (
                  <li key={i} className="flex items-center gap-1">
                    <span className="text-muted-foreground">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {hasMissing && compact && (
        <div className="text-xs text-muted-foreground truncate">
          Missing: {missingItems.slice(0, 2).join(', ')}
          {missingItems.length > 2 && ` +${missingItems.length - 2}`}
        </div>
      )}
    </div>
  );
}

export function ThreePartProgress({
  data,
  packet,
  compliance,
  className,
  compact = false,
}: ThreePartProgressProps) {
  const overallPercentage = Math.round((data.percentage + packet.percentage + compliance.percentage) / 3);
  const isReady = data.percentage >= 100 && packet.percentage >= 100 && compliance.percentage >= 100;

  if (compact) {
    return (
      <div className={cn('space-y-1', className)}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Overall Progress</span>
          <Badge variant={isReady ? 'default' : 'secondary'}>
            {overallPercentage}%
          </Badge>
        </div>
        <ProgressRow
          icon={ClipboardCheck}
          title="Data"
          description="fields answered"
          percentage={data.percentage}
          missingItems={data.missingItems}
          compact
        />
        <ProgressRow
          icon={FileStack}
          title="Packet"
          description="documents present"
          percentage={packet.percentage}
          missingItems={packet.missingItems}
          compact
        />
        <ProgressRow
          icon={ShieldCheck}
          title="Compliance"
          description="checks passing"
          percentage={compliance.percentage}
          missingItems={compliance.missingItems}
          compact
        />
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span>Permit Readiness</span>
          <Badge
            variant={isReady ? 'default' : 'outline'}
            className={cn(
              'font-mono text-sm',
              isReady && 'bg-green-500 hover:bg-green-600'
            )}
          >
            {isReady ? 'READY TO SUBMIT' : `${overallPercentage}% Complete`}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-0 divide-y">
        <ProgressRow
          icon={ClipboardCheck}
          title="Data Completeness"
          description="Required fields answered"
          percentage={data.percentage}
          missingItems={data.missingItems}
        />
        <ProgressRow
          icon={FileStack}
          title="Packet Completeness"
          description="Required documents uploaded"
          percentage={packet.percentage}
          missingItems={packet.missingItems}
        />
        <ProgressRow
          icon={ShieldCheck}
          title="Compliance Readiness"
          description="Validation checks passing"
          percentage={compliance.percentage}
          missingItems={compliance.missingItems}
        />
      </CardContent>
    </Card>
  );
}

/**
 * Calculate data completeness from job object
 * Returns percentage and list of missing fields
 */
export function calculateDataCompleteness(job: Record<string, unknown>): ProgressSection {
  const requiredFields = [
    { key: 'title', label: 'Job title' },
    { key: 'location', label: 'Property address' },
    { key: 'jurisdictionId', label: 'Jurisdiction' },
    { key: 'permitType', label: 'Permit type' },
    { key: 'client_name', label: 'Client name' },
  ];

  const optionalButImportantFields = [
    { key: 'jobScope', label: 'Job scope' },
    { key: 'estimatedValue', label: 'Project value' },
    { key: 'description', label: 'Job description' },
  ];

  const missingRequired: string[] = [];
  const missingOptional: string[] = [];

  requiredFields.forEach(({ key, label }) => {
    if (!job[key]) {
      missingRequired.push(label);
    }
  });

  optionalButImportantFields.forEach(({ key, label }) => {
    if (!job[key]) {
      missingOptional.push(label);
    }
  });

  // Required fields are worth 70%, optional worth 30%
  const requiredScore = ((requiredFields.length - missingRequired.length) / requiredFields.length) * 70;
  const optionalScore = ((optionalButImportantFields.length - missingOptional.length) / optionalButImportantFields.length) * 30;

  return {
    percentage: Math.round(requiredScore + optionalScore),
    missingItems: [...missingRequired, ...missingOptional.map((item) => `${item} (optional)`)],
    label: 'fields answered',
  };
}

/**
 * Calculate packet completeness from checklist and documents
 * Returns percentage and list of missing documents
 */
export function calculatePacketCompleteness(
  requiredItems: Array<{ name: string; isRequired: boolean }>,
  uploadedDocNames: string[]
): ProgressSection {
  if (!requiredItems || requiredItems.length === 0) {
    return {
      percentage: 0,
      missingItems: ['No requirements loaded'],
      label: 'documents present',
    };
  }

  const requiredDocs = requiredItems.filter((item) => item.isRequired);
  const optionalDocs = requiredItems.filter((item) => !item.isRequired);

  // Normalize document names for comparison
  const normalizeForComparison = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .replace(/pdf|doc|docx|jpg|png|jpeg/g, '');
  };

  const uploadedNormalized = uploadedDocNames.map(normalizeForComparison);

  const missingRequired: string[] = [];
  const missingOptional: string[] = [];

  requiredDocs.forEach((doc) => {
    const docNormalized = normalizeForComparison(doc.name);
    // Check if any uploaded doc contains this requirement name
    const isMatched = uploadedNormalized.some(
      (uploaded) => uploaded.includes(docNormalized) || docNormalized.includes(uploaded)
    );
    if (!isMatched) {
      missingRequired.push(doc.name);
    }
  });

  optionalDocs.forEach((doc) => {
    const docNormalized = normalizeForComparison(doc.name);
    const isMatched = uploadedNormalized.some(
      (uploaded) => uploaded.includes(docNormalized) || docNormalized.includes(uploaded)
    );
    if (!isMatched) {
      missingOptional.push(doc.name);
    }
  });

  // Required docs are worth 80%, optional 20%
  const requiredCount = requiredDocs.length || 1;
  const optionalCount = optionalDocs.length || 1;

  const requiredScore = ((requiredCount - missingRequired.length) / requiredCount) * 80;
  const optionalScore = ((optionalCount - missingOptional.length) / optionalCount) * 20;

  return {
    percentage: Math.round(requiredScore + optionalScore),
    missingItems: [
      ...missingRequired,
      ...missingOptional.map((item) => `${item} (optional)`),
    ],
    label: 'documents present',
  };
}

/**
 * Calculate compliance readiness from check results
 * Returns percentage and list of blockers
 */
export function calculateComplianceReadiness(
  checkResult?: {
    status: 'pass' | 'fail' | 'needs_review';
    score: number;
    findings: Array<{
      severity: 'critical' | 'warning' | 'info';
      title: string;
    }>;
  } | null
): ProgressSection {
  if (!checkResult) {
    return {
      percentage: 0,
      missingItems: ['Compliance check not run'],
      label: 'checks passing',
    };
  }

  const criticalFindings = checkResult.findings.filter((f) => f.severity === 'critical');
  const warningFindings = checkResult.findings.filter((f) => f.severity === 'warning');

  // Score is already provided by the backend
  let blockers: string[] = [];

  if (criticalFindings.length > 0) {
    blockers = criticalFindings.map((f) => f.title);
  } else if (warningFindings.length > 0 && checkResult.status !== 'pass') {
    blockers = warningFindings.slice(0, 3).map((f) => `${f.title} (warning)`);
  }

  return {
    percentage: checkResult.score,
    missingItems: blockers.length > 0 ? blockers : undefined,
    label: 'checks passing',
  };
}

export default ThreePartProgress;
